import { useEffect, useState } from "react";

export const numberFormatter = (
  num: number,
  decimal: number = 1,
  unit: string = ""
) => {
  const formatWithCommas = (value: string) => {
    const parts = value.split('.');
    const wholePart = parts[0];
    return wholePart.length >= 4 
      ? wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (parts[1] ? "." + parts[1] : "")
      : value;
  };
  if (num === 0) {
    return (unit + 0).trim();
  }
  let returnValue = "";
  let isNegative = num < 0;
  num = Math.abs(num);
  if (num > 0 && num < 1) {
    returnValue = (Number((num * 100).toFixed(decimal)) / 100).toFixed(decimal); // if value < 1000, nothing to do
  } else if (num > 999 && num < 1000000) {
    returnValue = formatWithCommas((num / 1000).toFixed(decimal)) + "K"; // convert to K for number from > 1000 < 1 million
  } else if (num >= 1000000 && num < 1000000000) {
    returnValue = formatWithCommas((num / 1000000).toFixed(decimal)) + "M"; // convert to M for number from >= 1 million
  } else if (num >= 1000000000) {
    //convert to B for number from >= 1B
    returnValue = formatWithCommas((num / 1000000000).toFixed(decimal)) + "B";
  } else {
    returnValue = num.toFixed(decimal);
  }
  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export const numberFormatterOnlyInM = (
  num: number,
  decimal: number = 1,
  unit: string = ""
) => {
  const formatWithCommas = (value: string) => {
    const parts = value.split('.');
    const wholePart = parts[0];
    return wholePart.length >= 4 
      ? wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (parts[1] ? "." + parts[1] : "")
      : value;
  };
  if (num === 0) {
    return (unit + 0).trim();
  }
  let returnValue = "";
  let isNegative = num < 0;
  num = Math.abs(num);
  if (num > 0 && num < 1) {
    returnValue = (Number((num * 100).toFixed(decimal)) / 100).toFixed(decimal); // if value < 1000, nothing to do
  } else if (num > 999 && num < 1000000) {
    returnValue = formatWithCommas((num / 1000).toFixed(decimal)) + "K"; // convert to K for number from > 1000 < 1 million
  } else if (num >= 1000000) {
    returnValue = formatWithCommas((num / 1000000).toFixed(decimal)) + "M"; // convert to M for number from >= 1 million
  } else {
    returnValue = num.toFixed(decimal);
  }
  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export const numberFormatterInK = (
  num: number,
  decimal: number = 1,
  unit: string = ""
) => {
  if (num === 0) {
    return (unit + 0).trim();
  }

  const isNegative = num < 0;
  num = Math.abs(num);

  let returnValue = "";
  if (num > 0 && num < 1000) {
    // No formatting for numbers less than 1000
    returnValue = num.toFixed(decimal);
  } else {
    // Convert to K format and add commas
    returnValue = `${(num / 1000)
      .toFixed(decimal)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}K`;
  }

  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export default function useDebounce(value: any, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const dateFormatterToMMDD = (dateStr: any) => {
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}`;
  return formattedDate;
};

export const dateFormatterToMMMYY = (dateStr: any) => {
  const date = new Date(dateStr);
  return date
    .toLocaleDateString("en-us", {
      month: "short",
      year: "2-digit",
    })
    .replace(" ", "-");
};

export const numberFormatterForDecimalInMillion = (
  num: number,
  decimal: number,
  unit: string = ""
) => {
  if (num === 0) {
    return (unit + 0).trim();
  }
  let returnValue = "";
  let isNegative = num < 0;
  num = Math.abs(num);
  if (num > 0 && num < 1) {
    returnValue = (Number((num * 100).toFixed(decimal)) / 100).toFixed(decimal); // if value < 1000, nothing to do
  } else if (num > 999 && num < 100000) {
    returnValue = (num / 1000).toFixed(decimal) + "K"; // convert to K for number from > 1000 < 1 million
  } else if (num >= 100000 && num < 1000000000) {
    returnValue = (num / 1000000).toFixed(decimal) + "M"; // convert to M for number from >= 1 million
  } else if (num >= 1000000000) {
    //convert to B for number from >= 1B
    returnValue = (num / 1000000000).toFixed(decimal) + "B";
  } else {
    returnValue = num.toFixed(decimal);
  }
  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export const formatNumbersToFourDigits = (num: number) => {
  const numStr = num.toString();

  if (numStr.includes(".")) {
    const [integerPart, decimalPart] = numStr.split(".");
    if (integerPart.length >= 4) {
      return parseInt(integerPart.slice(0, 4), 10);
    } else {
      const requiredDecimals = 4 - integerPart.length;
      return parseFloat(
        integerPart + "." + decimalPart.slice(0, requiredDecimals)
      );
    }
  } else {
    return parseInt(numStr.slice(0, 4), 10);
  }
};

export const calculateCommitmentGoalAchieved = (
  start: number,
  final: number
) => {
  let percentageCalculated = ((final - start) / Math.abs(start)) * 100;

  return parseFloat((100 + percentageCalculated).toFixed(1));
};
